import { MouseEvent } from "react";
import { useRouter } from "next/router";

import { CATEGORY_NAMES } from "constants/dispensary";
import { Category } from "constants/events";
import { SnackBarMessages, useSnackBar } from "context/SnackBarContext";
import { Coordinates } from "custom-types/Coordinates";
import { MenuItemCard } from "custom-types/MenuItemCard";
import MenuItemFilterSection from "custom-types/MenuItemFilterSection";
import useMenuItemEvents from "hooks/useMenuItemEvents";
import { useSelector } from "redux/hooks";
import { getIsValidStreetAddress } from "redux/selectors/location";

import Button from "components/botanic/Button";
import CoinList from "components/CoinList";
import { getMenuItemIndicator } from "components/Dispensary/MenuCard/MenuCard";
import HorizontalCardDeliveryFulfillmentInfo from "components/DispensaryFulfillmentInfo/HorizontalCardDeliveryFulfillmentInfo";
import DispensaryMenuItemCard from "components/DispensaryMenuItemCard/DispensaryMenuItemCard";
import HorizontalCardPickupFulfillmentInfo from "components/DispensaryPickupFulfillmentInfo/HorizontalCardPickupFulfillmentInfo";
import ItemCount from "components/ItemCount/ItemCount";
import { useFilterQueryParams } from "components/MenuFilters/v2/hooks";
import TrackImpression from "components/TrackImpression";

import ShopCoreSort from "../DeliveryGateway/ShopCoreSort";
import { SortState } from "../DeliveryGateway/ShopCoreSort/ShopCoreSort";
import MenuItemLoadingSkeleton from "./MenuItemLoadingSkeleton";
import MenuItemLoadingSkeletonMobile from "./MenuItemLoadingSkeletonMobile";
import MenuItemResultsStrainEffectCopy from "./MenuItemResultsStrainEffectCopy";
import { SponsoredMenuItemResults } from "./SponsoredMenuItemResults";

const MenuItemResults = ({
  className,
  coordinates,
  isPickupFulfillmentMode,
  isDeliveryFulfillmentMode,
  loading,
  menuItems,
  onClickLoadMore,
  onChangeSort,
  onShowAddressCaptureModal,
  selectedSort,
  hasPermanentFilters = false,
  totalCount,
  showEmptyResultsMessaging,
  eventCategory = Category.deliveryGateFlow,
  fulfillmentModeUnavailable,
  hideCoinList,
  showStrainEffectCopy,
  renderNoResults,
}: {
  className?: string;
  coordinates: Coordinates;
  isDeliveryFulfillmentMode?: boolean;
  isPickupFulfillmentMode?: boolean;
  loading: boolean;
  menuItems: MenuItemCard[];
  onClickLoadMore: () => Promise<void>;
  onChangeSort: (newVal: SortState | null) => void;
  onShowAddressCaptureModal: () => void;
  selectedSort: SortState | null;
  hasPermanentFilters?: boolean;
  totalCount: number;
  brandId?: number;
  brandName?: string;
  isFollowing?: boolean;
  showEmptyResultsMessaging?: boolean;
  eventCategory?: Category;
  fulfillmentModeUnavailable?: boolean;
  hideCoinList?: boolean;
  showStrainEffectCopy?: boolean;
  renderNoResults?: () => React.ReactNode;
}) => {
  const {
    appliedFilters,
    actions: { toggleFilter },
  } = useFilterQueryParams();
  const filtersApplied = Object.keys(appliedFilters).length > 0;
  const {
    query: { q },
  } = useRouter();
  const { addSnackBarItem } = useSnackBar();
  const isValidAddress = useSelector(getIsValidStreetAddress);
  const displayShowMoreButton = totalCount > menuItems.length;
  const hasFiltersButNoAddress =
    filtersApplied && !isValidAddress && !isPickupFulfillmentMode;
  const { publishMenuItemClickEvent, publishMenuItemImpressionEvent } =
    useMenuItemEvents();
  const showSponsoredResults =
    !hasPermanentFilters &&
    selectedSort === null &&
    isDeliveryFulfillmentMode &&
    !Object.keys(appliedFilters).includes("dispensary_name") &&
    !q;

  const handleCardClick = (event: MouseEvent, menuItem: MenuItemCard) => {
    if (hasFiltersButNoAddress) {
      event?.preventDefault();
      onShowAddressCaptureModal();
    } else {
      publishMenuItemClickEvent(menuItem, eventCategory);
    }
  };

  const handleAddToBagClick = (menuItem: MenuItemCard) => {
    const dispensaryNameFilterValue =
      appliedFilters[MenuItemFilterSection.DispensaryName];
    const isDispensaryFiltered = Array.isArray(dispensaryNameFilterValue)
      ? dispensaryNameFilterValue.includes(menuItem.dispensary.name)
      : dispensaryNameFilterValue === menuItem.dispensary.name;
    if (!isDispensaryFiltered) {
      addSnackBarItem(SnackBarMessages.resultsFilteredToDispensary);
      toggleFilter(
        MenuItemFilterSection.DispensaryName,
        menuItem.dispensary.name,
      );
    }
  };

  return (
    <div className={className} data-testid="shop-product-results">
      <div className="flex justify-between">
        <ItemCount itemCount={totalCount} />
        {!!menuItems?.length && (
          <ShopCoreSort
            selectedSort={selectedSort}
            onChange={
              hasFiltersButNoAddress ? onShowAddressCaptureModal : onChangeSort
            }
            isDeliveryFulfillmentMode={isDeliveryFulfillmentMode}
          />
        )}
      </div>
      {!isDeliveryFulfillmentMode &&
        showEmptyResultsMessaging &&
        !showStrainEffectCopy && (
          <h3 className="hidden md:block pb-lg">
            Try ordering for in-store pickup.
          </h3>
        )}
      {showStrainEffectCopy && !!menuItems.length && (
        <MenuItemResultsStrainEffectCopy
          menuItems={menuItems}
          isPickup={!!isPickupFulfillmentMode}
        />
      )}
      {!fulfillmentModeUnavailable && (
        <hr className="border-light-grey -mx-lg mt-sm mb-xl md:hidden" />
      )}
      {showSponsoredResults && (
        <SponsoredMenuItemResults
          onShowAddressCaptureModal={onShowAddressCaptureModal}
          isDeliveryFulfillmentMode={isDeliveryFulfillmentMode}
          coordinates={coordinates}
          className="-mt-sm md:mt-0"
        />
      )}

      <div className="grid grid-cols-1 mb-xxl mt-sm gap-md">
        {loading ? (
          <>
            <div className="hidden md:block">
              <MenuItemLoadingSkeleton />
            </div>
            <div className="block md:hidden">
              <MenuItemLoadingSkeletonMobile />
            </div>
          </>
        ) : menuItems?.length ? (
          menuItems.map((menuItem) => (
            <TrackImpression
              key={menuItem.id}
              className="h-full"
              trackingFunction={() =>
                publishMenuItemImpressionEvent(menuItem, eventCategory)
              }
            >
              <DispensaryMenuItemCard
                menuItem={menuItem}
                menuCardIndicators={getMenuItemIndicator(menuItem)}
                handleCardClick={(e) => {
                  handleCardClick(e, menuItem);
                }}
                useAlternateAddToBagAction={hasFiltersButNoAddress}
                onAlternateAddToBagClick={onShowAddressCaptureModal}
                renderFulfillmentInfo={(dispensary) =>
                  isDeliveryFulfillmentMode ? (
                    <HorizontalCardDeliveryFulfillmentInfo
                      dispensary={dispensary}
                    />
                  ) : (
                    <HorizontalCardPickupFulfillmentInfo
                      dispensary={dispensary}
                    />
                  )
                }
                onReservation={() => {
                  handleAddToBagClick(menuItem);
                }}
                eventCategory={eventCategory}
              />
            </TrackImpression>
          ))
        ) : (
          <>{renderNoResults && renderNoResults()}</>
        )}
        {!hideCoinList && menuItems?.length > 0 && (
          <CoinList
            coins={Object.keys(CATEGORY_NAMES)}
            hrefLink="/delivery"
            hrefProductCatQueryKey="filter[product_category]"
            title="Shop by category"
          />
        )}
      </div>
      {displayShowMoreButton ? (
        <Button
          variant="secondary"
          className="mb-xxl"
          onClick={onClickLoadMore}
        >
          show more results
        </Button>
      ) : null}
    </div>
  );
};
export default MenuItemResults;
